import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { CHART_INK, formatDayLabel, formatUsdCompact, formatUsdFull } from './common';

export interface StackedBarSeries {
  dataKey: string;
  name: string;
  color: string;
  /**
   * Which stack this series belongs to. Series sharing an id stack together;
   * different ids sit side by side.
   *
   * Only stack things that genuinely sum to something. Deposits stacked on
   * withdrawals, or cash received stacked on cash expected, draw a total that
   * means nothing — those belong in separate stacks.
   */
  stackId?: string;
}

interface StackedBarChartProps {
  data: Array<Record<string, number | string>>;
  series: StackedBarSeries[];
  xKey?: string;
  labelFormatter?: (value: string) => string;
}

/** Per-bucket amounts, stacked within a group and grouped across stacks. */
const StackedBarChart: React.FC<StackedBarChartProps> = ({
  data,
  series,
  xKey = 'date',
  labelFormatter = formatDayLabel,
}) => (
  <ResponsiveContainer width="100%" height="100%">
    <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
      <CartesianGrid strokeDasharray="3 3" stroke={CHART_INK.grid} vertical={false} />
      <XAxis
        dataKey={xKey}
        tickFormatter={labelFormatter}
        tick={{ fontSize: 11, fill: CHART_INK.muted }}
        tickLine={false}
        axisLine={false}
        minTickGap={16}
      />
      <YAxis
        tickFormatter={formatUsdCompact}
        tick={{ fontSize: 11, fill: CHART_INK.muted }}
        tickLine={false}
        axisLine={false}
        width={70}
      />
      <Tooltip
        labelFormatter={(label) => labelFormatter(String(label))}
        formatter={(value: number, name: string) => [formatUsdFull(value), name]}
        contentStyle={{ borderRadius: 10, border: `1px solid ${CHART_INK.grid}`, fontSize: 12 }}
      />
      <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" iconSize={8} />
      {series.map((s, index) => {
        const stackId = s.stackId ?? 'stack';
        // Only the top series of each stack gets a rounded cap.
        const isTopOfStack = !series.some(
          (other, i) => i > index && (other.stackId ?? 'stack') === stackId,
        );
        return (
          <Bar
            isAnimationActive={false}
            key={s.dataKey}
            dataKey={s.dataKey}
            name={s.name}
            stackId={stackId}
            fill={s.color}
            maxBarSize={36}
            radius={isTopOfStack ? [3, 3, 0, 0] : [0, 0, 0, 0]}
          />
        );
      })}
    </BarChart>
  </ResponsiveContainer>
);

export default StackedBarChart;
